import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Pencil, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tvKey } from './tvKeys';
import { LOGO_URL, popNav, popOk, isNouveaute, isKidBanner, isKidProfile, mixByType, shuffle, preloadImg, TV_BUILD, canExitViaBack } from './tvAssets';
import { TvProfileEdit, TvNameKeyboard } from './TvProfileEdit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KID_RATINGS = ['Tout public', '7+', '10+'];
const MAX_PROFILES = 5;

function Avatar({ p, focused }) {
  return (
    <div className={`tv-card ${focused ? 'tv-focused' : ''}`}
      style={{ width: '6.8vw', height: '6.8vw', borderRadius: '0.7vw', background: p.avatar_color || '#E9641A',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.7vw', fontWeight: 800,
        opacity: focused ? 1 : 0.9, position: 'relative', overflow: 'hidden' }}>
      {p.avatar_url
        ? <img src={p.avatar_url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : (p.name || '?')[0].toUpperCase()}
      {isKidProfile(p) && (
        <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', fontSize: '0.85vw', fontWeight: 800,
          padding: '0.2vw 0', background: '#2D6BFF', color: '#fff', letterSpacing: '0.04em' }}
          data-testid={`tv-kid-tag-${p.id}`}>Enfant</span>
      )}
    </div>
  );
}

export function TvProfiles({ onSelect, onExitRequest }) {
  const { user, setActiveProfile, checkAuth } = useAuth();
  const profiles = user?.profiles || [];
  const canAdd = profiles.length < MAX_PROFILES;
  const [idx, setIdx] = useState(0);
  const [zone, setZone] = useState('profiles'); // profiles | edit
  const [editing, setEditing] = useState(null);
  const [addStep, setAddStep] = useState(null); // null | 'kind' | 'name'
  const [addKind, setAddKind] = useState('adult');
  const [notice, setNotice] = useState('');
  const addOpen = addStep !== null;
  const [leaving, setLeaving] = useState(null); // profile id being selected (transition running)

  const selectProfile = useCallback((p) => {
    popOk();
    if (onSelect) {
      const el = document.querySelector(`[data-testid="tv-avatar-${p.id}"]`);
      const rect = el ? el.getBoundingClientRect()
        : { left: window.innerWidth / 2 - 40, top: window.innerHeight / 2 - 40, width: 80, height: 80 };
      setLeaving(p.id);
      onSelect(p, rect);
    } else {
      setActiveProfile(p);
    }
  }, [onSelect, setActiveProfile]);
  const [pool, setPool] = useState(null); // { featured, catalog } — set ONCE so the banner list is not reshuffled mid-display
  const [bgIdx, setBgIdx] = useState(0);

  const maxIdx = profiles.length - 1 + (canAdd ? 1 : 0);
  useEffect(() => { setIdx(i => Math.min(i, Math.max(0, maxIdx))); }, [maxIdx]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/contents/featured`, { withCredentials: true }).then(r => r.data || []).catch(() => []),
      axios.get(`${API}/contents`, { withCredentials: true }).then(r => r.data || []).catch(() => []),
    ]).then(([featured, catalog]) => setPool({ featured, catalog }));
  }, []);

  // Banners adapted to the focused profile: kid -> jeunesse (incl. séries/docs jeunesse), adult -> the rest
  // All types mixed (fiction, séries, documentaires…), new releases first within each type
  const focusedProfile = profiles[Math.min(idx, Math.max(0, profiles.length - 1))];
  const kidFocused = isKidProfile(focusedProfile);
  const banners = useMemo(() => {
    if (!pool) return [];
    const allow = c => (kidFocused ? isKidBanner(c) : (!isKidBanner(c) && c.type !== 'game'))
      && (c.tv_banner_url || c.banner_url || c.thumbnail_url);
    const seen = new Set();
    const list = [...pool.catalog, ...pool.featured].filter(c => {
      if (!allow(c) || seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
    const nouveautes = shuffle(list.filter(isNouveaute));
    const rest = shuffle(list.filter(c => !isNouveaute(c)));
    return mixByType([...nouveautes, ...rest]).slice(0, 12);
  }, [pool, kidFocused]);

  useEffect(() => { setBgIdx(0); setMountedBanners(new Set([0, 1])); }, [kidFocused]);

  // Progressive banner loading: only the active + next banner images are mounted (then they stay),
  // so the first banner is not slowed down by 11 other simultaneous downloads
  const [mountedBanners, setMountedBanners] = useState(() => new Set([0, 1]));
  useEffect(() => {
    setMountedBanners(m => {
      const n = new Set(m);
      n.add(bgIdx);
      if (banners.length) n.add((bgIdx + 1) % banners.length);
      return n;
    });
  }, [bgIdx, banners.length]);

  // Warm up the next banner image + title logos so title/logo appear instantly
  useEffect(() => {
    if (!banners.length) return;
    [banners[bgIdx % banners.length], banners[(bgIdx + 1) % banners.length]].forEach(b => {
      if (!b) return;
      preloadImg(b.logo_url);
      preloadImg(b.tv_banner_url || b.banner_url || b.thumbnail_url);
    });
  }, [bgIdx, banners]);

  // First banner = entrance animation only; classic banner animation kicks in from the first rotation
  const [entered, setEntered] = useState(false);
  useEffect(() => { if (bgIdx !== 0) setEntered(true); }, [bgIdx]);

  // The 5s countdown of the FIRST banner starts only once its entrance animation is done
  const [introDone, setIntroDone] = useState(false);
  useEffect(() => { const t = setTimeout(() => setIntroDone(true), 1200); return () => clearTimeout(t); }, []);

  // Change the banner on every profile navigation (StrictMode-safe: only on a real idx change)
  const prevIdx = React.useRef(idx);
  useEffect(() => {
    if (prevIdx.current === idx) return;
    prevIdx.current = idx;
    setBgIdx(i => (banners.length ? (i + 1) % banners.length : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // 5s per banner: the timer restarts each time the banner changes (incl. profile navigation)
  // and only starts counting after the entrance animation of the first banner
  useEffect(() => {
    if (banners.length < 2 || editing || !introDone) return;
    const t = setTimeout(() => setBgIdx(i => (i + 1) % banners.length), 5000);
    return () => clearTimeout(t);
  }, [bgIdx, banners.length, editing, introDone]);

  const openEdit = useCallback((p) => setEditing(p), []);

  const tryAdd = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profiles/can-add`, { withCredentials: true });
      if (!data.allowed) { setNotice(data.message); return; }
    } catch { /* en cas d'erreur réseau on laisse passer, le backend re-validera */ }
    setAddKind('adult');
    setAddStep('kind');
  }, []);

  const createProfile = useCallback(async (name) => {
    setAddStep(null);
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/profiles`, { name: name.trim(), avatar_url: '', kind: addKind }, { withCredentials: true });
      await checkAuth();
    } catch (e) {
      setNotice(e.response?.data?.detail || 'Impossible de créer le profil.');
    }
  }, [checkAuth, addKind]);

  useEffect(() => {
    const onKey = (e) => {
      if (editing || addOpen || leaving || notice) return;
      const k = tvKey(e);
      if (!k) return;
      if (k === 'back') {
        e.preventDefault();
        // Fire TV (exigence Amazon) : Retour sur l'écran profils ouvre le dialogue de sortie
        if (canExitViaBack && onExitRequest) { popNav(); onExitRequest(); }
        return;
      }
      if (k === 'up') { e.preventDefault(); if (idx > 0) popNav(); setIdx(i => Math.max(0, i - 1)); }
      else if (k === 'down') { e.preventDefault(); if (idx < maxIdx) popNav(); setIdx(i => Math.min(maxIdx, i + 1)); }
      else if (k === 'right') { e.preventDefault(); if (idx < profiles.length) { if (zone !== 'edit') popNav(); setZone('edit'); } }
      else if (k === 'left') { e.preventDefault(); if (zone !== 'profiles') popNav(); setZone('profiles'); }
      else if (k === 'ok') {
        e.preventDefault();
        if (canAdd && idx === profiles.length) { popOk(); tryAdd(); return; }
        const p = profiles[idx];
        if (!p) return;
        if (zone === 'profiles') { selectProfile(p); }
        else { popOk(); openEdit(p); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (editing) {
    return <TvProfileEdit profile={editing} canDelete={profiles.length > 1}
      onClose={() => { setEditing(null); setZone('profiles'); }} />;
  }

  const current = banners[bgIdx % Math.max(1, banners.length)];
  const showLogo = current && current.tv_banner_url && current.logo_url;

  return (
    <div className="tv-fade-in" style={{ position: 'relative', height: '100%', overflow: 'hidden',
      opacity: leaving ? 0 : 1, transition: 'opacity 0.6s ease' }} data-testid="tv-profiles">
      <span style={{ position: 'absolute', bottom: '1vw', right: '1.4vw', zIndex: 5, fontSize: '0.9vw', color: 'rgba(255,255,255,0.35)', fontWeight: 600 }} data-testid="tv-build-tag">{TV_BUILD}</span>
      {/* Rotating background banners (landscape movie on the right) — intro: scale 0→1 */}
      <div className="tv-intro-banner" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
        {banners.map((b, i) => (
          <div key={b.id} className={`tv-bg-layer ${i === bgIdx ? 'tv-bg-active' : ''} ${!entered && i === bgIdx ? 'tv-bg-noanim' : ''}`}>
            {mountedBanners.has(i) && <img src={b.tv_banner_url || b.banner_url || b.thumbnail_url} alt="" />}
          </div>
        ))}
      </div>
      {/* Dark purple gradient over the left side + bottom */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background:
        'linear-gradient(90deg, #0b0612 0%, rgba(13,6,22,0.98) 24%, rgba(20,8,38,0.82) 42%, rgba(11,6,18,0.3) 68%, rgba(11,6,18,0.05) 100%), linear-gradient(0deg, rgba(11,6,18,0.92) 0%, rgba(11,6,18,0.4) 18%, transparent 40%)' }} />

      {/* Logo — intro: slides in from off-screen left */}
      <div className="tv-intro-logo" style={{ position: 'absolute', top: '2.2vw', left: '4vw', zIndex: 2 }}>
        <img src={LOGO_URL} alt="Maya+" style={{ height: '2.6vw', filter: 'drop-shadow(0 0.1vw 1vw rgba(0,0,0,0.6))' }} />
      </div>

      {/* Profiles stacked vertically on the left */}
      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: '4vw', paddingTop: '5.5vw', paddingBottom: '1vw' }}>
        <h1 className="tv-intro-title" style={{ fontSize: '2.3vw', fontWeight: 700, marginBottom: '1.6vw', textShadow: '0 0.15vw 1vw rgba(0,0,0,0.7)' }}>Qui est-ce ?</h1>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {profiles.map((p, i) => {
            const rowActive = i === idx;
            return (
              <div key={p.id} className="tv-intro-profile" style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5vw', animationDelay: `${0.8 + i * 0.12}s` }} data-testid={`tv-profile-${p.id}`}>
                <div onClick={() => selectProfile(p)} data-testid={`tv-avatar-${p.id}`}
                  style={{ visibility: leaving === p.id ? 'hidden' : 'visible', flex: 'none' }}>
                  <Avatar p={p} focused={zone === 'profiles' && rowActive} />
                </div>
                <span style={{ fontSize: '1.9vw', fontWeight: 600, minWidth: '11vw', maxWidth: '15vw', marginLeft: '1.6vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: rowActive ? '#fff' : 'rgba(255,255,255,0.65)', textShadow: '0 0.1vw 0.6vw rgba(0,0,0,0.7)' }}>{p.name}</span>
                <div className={`tv-btn ${zone === 'edit' && rowActive ? 'tv-focused' : ''}`} onClick={() => { popOk(); openEdit(p); }}
                  style={{ width: '3vw', height: '3vw', marginLeft: '1.6vw', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
                    background: zone === 'edit' && rowActive ? '#E9641A' : 'rgba(255,255,255,0.12)', cursor: 'pointer',
                    opacity: rowActive ? 1 : 0.45 }}
                  data-testid={`tv-profile-edit-${p.id}`}>
                  <Pencil style={{ width: '1.3vw', height: '1.3vw' }} />
                </div>
              </div>
            );
          })}
          {canAdd && (
            <div className="tv-intro-profile" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', animationDelay: `${0.8 + profiles.length * 0.12}s` }}
              onClick={() => { popOk(); setAddKind('adult'); setAddStep('kind'); }} data-testid="tv-add-profile">
              <div className={`tv-card ${idx === profiles.length ? 'tv-focused' : ''}`}
                style={{ width: '6.8vw', height: '6.8vw', borderRadius: '0.7vw', background: 'rgba(255,255,255,0.07)', flex: 'none',
                  border: '0.15vw dashed rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Plus style={{ width: '2.4vw', height: '2.4vw', color: 'rgba(255,255,255,0.75)' }} />
              </div>
              <span style={{ fontSize: '1.6vw', fontWeight: 600, marginLeft: '1.6vw', color: idx === profiles.length ? '#fff' : 'rgba(255,255,255,0.55)' }}>
                Ajouter un profil
              </span>
            </div>
          )}
        </div>
        <p style={{ marginTop: '1.6vw', fontSize: '1vw', color: 'rgba(255,255,255,0.4)' }}>
          Appuyez sur ▶ puis OK pour modifier un profil
        </p>
      </div>

      {/* Banner info: title logo (or title) + tags, bottom-right */}
      {current && (
        <div key={current.id} className="tv-fade-in" style={{ position: 'absolute', right: '4vw', bottom: '2.5vw', maxWidth: '34vw', zIndex: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }} data-testid="tv-profiles-banner-info">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {((current.categories || []).includes('originals') || current.category === 'originals') && (
              <img src={LOGO_URL} alt="Maya+" data-testid="tv-profiles-original-logo"
                style={{ height: '3.3vw', width: 'auto', marginBottom: '0.55vw', filter: 'drop-shadow(0 0.1vw 0.3vw rgba(0,0,0,0.9))' }} />
            )}
            {showLogo ? (
              <img src={current.logo_url} alt={current.title} className="tv-logo-slide"
                style={{ maxHeight: '9.75vw', maxWidth: '32vw', objectFit: 'contain', marginBottom: '0.8vw' }}
                data-testid="tv-profiles-banner-logo" />
            ) : (
              <p style={{ fontSize: '2.25vw', fontWeight: 800, marginBottom: '0.6vw', textAlign: 'center', lineHeight: 1.15,
                overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                textShadow: '0 0.15vw 0.9vw rgba(0,0,0,0.85)' }}>{current.title}</p>
            )}
          </div>
          {(current.tags || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {current.tags.slice(0, 4).map(t => (
                <span key={t} style={{ fontSize: '1.25vw', padding: '0.35vw 1.2vw', borderRadius: '2vw', margin: '0.25vw 0 0 0.5vw',
                  background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(6px)' }}>
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {addStep === 'kind' && (
        <TvKindPicker kind={addKind} setKind={setAddKind}
          onDone={() => setAddStep('name')} onCancel={() => setAddStep(null)} />
      )}
      {addStep === 'name' && <TvNameKeyboard initial="" onDone={createProfile} onCancel={() => setAddStep(null)} />}
      {notice && <TvNotice message={notice} onClose={() => setNotice('')} />}
    </div>
  );
}

function TvNotice({ message, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      if (k === 'ok' || k === 'back') { popOk(); onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 95, background: 'rgba(8,3,15,0.96)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-notice">
      <p style={{ fontSize: '1.6vw', fontWeight: 800, color: '#fff', marginBottom: '1vw' }}>Limite de profils atteinte</p>
      <p style={{ fontSize: '1.15vw', color: 'rgba(255,255,255,0.75)', maxWidth: '46vw', textAlign: 'center', lineHeight: 1.6, marginBottom: '2vw' }} data-testid="tv-notice-message">{message}</p>
      <div className="tv-btn tv-focused" onClick={onClose} data-testid="tv-notice-ok"
        style={{ padding: '0.7vw 3vw', borderRadius: '2vw', background: '#E9641A', color: '#fff', fontSize: '1.1vw', fontWeight: 700, cursor: 'pointer' }}>
        OK
      </div>
    </div>
  );
}

function TvKindPicker({ kind, setKind, onDone, onCancel }) {
  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      if (k === 'left' || k === 'right' || k === 'up' || k === 'down') { popNav(); setKind(kind === 'adult' ? 'kids' : 'adult'); }
      else if (k === 'ok') { popOk(); onDone(); }
      else if (k === 'back') { onCancel(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [kind, setKind, onDone, onCancel]);

  const Card = ({ id, title, desc, color }) => {
    const focused = kind === id;
    return (
      <div onClick={() => { setKind(id); popOk(); onDone(); }} data-testid={`tv-profile-kind-${id}`}
        className={focused ? 'tv-focused' : ''}
        style={{ width: '18vw', padding: '1.6vw', borderRadius: '0.9vw', cursor: 'pointer',
          background: focused ? `${color}1f` : 'rgba(255,255,255,0.05)',
          border: `0.18vw solid ${focused ? color : 'rgba(255,255,255,0.12)'}`,
          transform: focused ? 'scale(1.05)' : 'scale(1)', transition: 'transform 0.2s ease, border-color 0.2s ease' }}>
        <p style={{ fontSize: '1.5vw', fontWeight: 800, color: '#fff', marginBottom: '0.5vw' }}>{title}</p>
        <p style={{ fontSize: '0.95vw', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{desc}</p>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: 'rgba(8,3,15,0.96)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-kind-picker">
      <h2 style={{ fontSize: '2vw', fontWeight: 700, marginBottom: '2vw', color: '#fff' }}>Type de profil</h2>
      <div style={{ display: 'flex' }}>
        <Card id="adult" title="Adulte" desc="Accès à tout le catalogue" color="#E9641A" />
        <div style={{ width: '2vw', flex: 'none' }} />
        <Card id="kids" title="Enfant" desc="Uniquement les films, séries jeunesse et jeux" color="#75D4D5" />
      </div>
      <p style={{ marginTop: '2.2vw', fontSize: '0.9vw', color: 'rgba(255,255,255,0.4)' }}>
        ◀ ▶ pour choisir · OK pour valider · Retour pour annuler
      </p>
    </div>
  );
}
